import React, { useState } from "react";
import { FiChevronDown, FiChevronRight, FiWifi, FiRadio, FiCpu, FiServer } from "react-icons/fi";
import { MdSettingsInputAntenna } from "react-icons/md";
import { deviceDummyData, allDevices } from "../../../Data/DeviceData";
import { statusMeta, connMeta, roleMeta, healthColor, HealthRing, BatteryBar, SignalBars } from "./deviceHelpers";

// Group devices under their gateways; standalone devices shown separately
const buildTopology = () => {
  const gateways = deviceDummyData.gateways;
  const allDevs  = allDevices;

  const tree = gateways.map(gw => ({
    ...gw,
    children: (gw.childDeviceIds || [])
      .map(id => allDevs.find(x => x.id === id))
      .filter(Boolean),
  }));

  // Standalone devices (no gatewayId and not gateways)
  const standalone = [...deviceDummyData.sensors, ...deviceDummyData.controllers]
    .filter(d => !d.gatewayId);

  return { tree, standalone };
};

// ── Device Node Card ──────────────────────────────────────────────────────────
const DeviceNode = ({ device, indent = 0, isChild = false, onClick, selected }) => {
  const sm = statusMeta(device.status);
  const cm = connMeta(device.connectivity);
  const rm = roleMeta(device.role);
  const hc = healthColor(device.healthScore);
  const isSelected = selected?.id === device.id;

  return (
    <div
      className={`dvc-topo-node ${isChild ? "dvc-topo-child" : "dvc-topo-gw"} ${isSelected ? "dvc-topo-selected" : ""}`}
      style={{ marginLeft: indent }}
      onClick={() => onClick(isSelected ? null : device)}
    >
      {isChild && <div className="dvc-topo-connector" />}
      <div className="dvc-topo-node-inner">
        <div className="dvc-topo-role-dot" style={{ background: rm.color, flexShrink: 0 }}>{rm.letter}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{device.name}</div>
          <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{device.id} · {device.type}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <cm.Icon size={13} color={cm.color} />
          <span className="dvc-topo-status" style={{ background: sm.bg, color: sm.color }}>
            <sm.icon size={10} />{device.status}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 28, height: 5, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${device.healthScore}%`, height: "100%", background: hc, borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: hc }}>{device.healthScore}</span>
          </div>
          {device.battery !== null && <span style={{ fontSize: 11, color: device.battery < 30 ? "#ef4444" : "#6b7280" }}>🔋{device.battery}%</span>}
        </div>
      </div>
    </div>
  );
};

// ── Gateway Tree ──────────────────────────────────────────────────────────────
const GatewayTree = ({ gateway, onClick, selected }) => {
  const [expanded, setExpanded] = useState(true);
  const sm = statusMeta(gateway.status);
  const cm = connMeta(gateway.connectivity);
  const hc = healthColor(gateway.healthScore);
  const isSelected = selected?.id === gateway.id;

  return (
    <div className="dvc-topo-gw-group">
      {/* Gateway header */}
      <div
        className={`dvc-topo-gw-header ${isSelected ? "dvc-topo-selected" : ""}`}
        onClick={() => onClick(isSelected ? null : gateway)}
      >
        <button className="dvc-topo-expand" onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}>
          {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        </button>
        <div className="dvc-topo-gw-icon" style={{ background: cm.color + "22", color: cm.color }}>
          <cm.Icon size={16} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{gateway.name}</div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>{gateway.id} · {gateway.connectivity} · {gateway.site}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HealthRing score={gateway.healthScore} size={40} />
          <span className="dvc-topo-status" style={{ background: sm.bg, color: sm.color, padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
            <sm.icon size={12} /> {gateway.status}
          </span>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{gateway.children?.length || 0} devices</div>
        </div>
      </div>

      {/* Children */}
      {expanded && gateway.children && (
        <div className="dvc-topo-children">
          {gateway.children.map(child => (
            <DeviceNode key={child.id} device={child} indent={0} isChild={true} onClick={onClick} selected={selected} />
          ))}
          {gateway.children.length === 0 && (
            <div className="dvc-topo-empty-children">No child devices registered</div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Detail Side Panel ─────────────────────────────────────────────────────────
const TopoDetail = ({ device, onOpenDetail }) => {
  if (!device) return (
    <div className="dvc-topo-nosel">
      <div style={{ fontSize: 40 }}>🌐</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginTop: 8 }}>Select a device</div>
      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>Click any device to view details</div>
    </div>
  );

  const sm = statusMeta(device.status);
  const cm = connMeta(device.connectivity);
  const hc = healthColor(device.healthScore);

  return (
    <div className="dvc-topo-detail">
      <div className="dvc-topo-det-header">
        <HealthRing score={device.healthScore} size={56} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{device.name}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{device.id}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <span style={{ padding: "2px 8px", borderRadius: 12, background: sm.bg, color: sm.color, fontSize: 11, fontWeight: 700 }}>{device.status}</span>
            <span style={{ padding: "2px 8px", borderRadius: 12, background: cm.bg, color: cm.color, fontSize: 11, fontWeight: 700 }}>{device.connectivity}</span>
          </div>
        </div>
      </div>
      <div className="dvc-topo-det-rows">
        {[
          { label: "Type",        val: device.type },
          { label: "Site",        val: device.site },
          { label: "Solution",    val: device.assignedSolution },
          { label: "Firmware",    val: device.firmware },
          { label: "Last Seen",   val: device.lastSeen },
          { label: "Signal",      val: device.signal !== null ? `${device.signal} dBm` : "N/A" },
        ].map(r => (
          <div key={r.label} className="dvc-info-row">
            <div className="dvc-info-lbl">{r.label}</div>
            <div className="dvc-info-val">{r.val || "—"}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <BatteryBar pct={device.battery} />
      </div>
      {device.telemetry && Object.keys(device.telemetry).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="dvc-section-title" style={{ fontSize: 12 }}>Latest Readings</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
            {Object.entries(device.telemetry).filter(([, v]) => v !== null).slice(0, 6).map(([k, v]) => (
              <div key={k} style={{ padding: "4px 10px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{typeof v === "number" ? v : String(v)}</div>
                <div style={{ fontSize: 10, color: "#9ca3af" }}>{k}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* AI note */}
      <div style={{ marginTop: 12, padding: 8, background: "#f0fdf4", borderRadius: 8, fontSize: 12, color: "#374151" }}>
        🤖 AI: {device.healthScore >= 85 ? "Device operating normally." : device.healthScore >= 60 ? "Monitor closely — signal or battery may need attention." : "Action required — health degraded. Schedule maintenance."}
      </div>
      {onOpenDetail && (
        <button className="dvc-ctrl-btn dvc-ctrl-purple" style={{ marginTop: 10, width: "100%", justifyContent: "center" }}
          onClick={() => onOpenDetail(device)}>
          Open Full Device Detail →
        </button>
      )}
    </div>
  );
};

// ── Main Panel ────────────────────────────────────────────────────────────────
const DvcTopologyPanel = ({ onSelectDevice, selectedDeviceId }) => {
  const [selected, setSelected] = useState(null);
  const { tree, standalone } = buildTopology();

  const handleClick = (device) => {
    setSelected(device === selected ? null : device);
  };

  const openDetail = (device) => {
    if (onSelectDevice) onSelectDevice(device);
  };

  return (
    <div className="dvc-topology-panel">
      <div className="dvc-topo-legend">
        <div className="dvc-section-title">Network Topology — Gateway Hierarchy</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {[{ label: "Gateway", color: "#667eea" }, { label: "Sensor", color: "#10b981" }, { label: "Controller", color: "#f97316" }].map(l => (
            <span key={l.label} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: l.color, display: "inline-block" }} />
              {l.label}
            </span>
          ))}
          <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: "auto" }}>Click any device to view details</span>
        </div>
      </div>

      <div className="dvc-topo-body">
        <div className="dvc-topo-tree">
          {/* Gateway trees */}
          {tree.map(gw => (
            <GatewayTree key={gw.id} gateway={gw} onClick={setSelected} selected={selected} />
          ))}

          {/* Standalone devices */}
          {standalone.length > 0 && (
            <div className="dvc-topo-gw-group">
              <div className="dvc-topo-standalone-header">
                <FiServer size={14} color="#6b7280" /> Direct-Connected / Standalone Devices
              </div>
              {standalone.map(dv => (
                <DeviceNode key={dv.id} device={dv} indent={0} isChild={false} onClick={setSelected} selected={selected} />
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <TopoDetail device={selected} onOpenDetail={openDetail} />
      </div>
    </div>
  );
};

export default DvcTopologyPanel;
